import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';
import { getOrCreateAppUser } from '../utils/supabaseUtils';

const MagicLinkHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  useEffect(() => {
    const handleMagicLinkAuth = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      
      // Log the current URL for debugging
      console.log("MagicLinkHandler - Current URL:", window.location.href);
      console.log("MagicLinkHandler - URL parameters:", params.toString());
      
      // Handle expired/used magic link from hash
      const expiredHash = window.location.hash;
      if (expiredHash && expiredHash.includes('error_code=otp_expired')) {
        toast({
          title: 'Link expired',
          description: 'That sign-in link has already been used. Please request a new one.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
          position: 'top',
        });

        // Navigate to login with proper history replacement
        navigate('/login?reason=expired', { replace: true });
        return;
      }

      // Handle access_token variant (hash-based auth)
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        const access_token = hashParams.get('access_token');
        const refresh_token = hashParams.get('refresh_token');

        if (access_token && refresh_token) {
          try {
            // Create Supabase session from the tokens
            const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
            
            if (error) {
              console.error('Error setting session from hash tokens:', error);
              navigate('/login', { replace: true });
              return;
            }
            
            // Ensure app user exists
            await getOrCreateAppUser();
            
            // Wait a moment for the auth state to propagate
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Navigate to dashboard with history replacement
            navigate('/dashboard', { replace: true });
          } catch (error) {
            console.error('Error setting session from hash tokens:', error);
            navigate('/login', { replace: true });
          }
          return;
        }
      }

      // Handle PKCE code-based auth (main magic link flow)
      if (code && !location.pathname.startsWith('/login')) {
        console.log("MagicLinkHandler - Magic link code detected:", code);
        
        try {
          // Exchange the code for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          
          if (error) {
            console.error("MagicLinkHandler - Error exchanging code for session:", error);
            toast({
              title: "Authentication failed",
              description: "There was a problem logging you in. Please try again.",
              status: "error",
              duration: 5000,
              isClosable: true,
              position: "top",
            });
            
            // Navigate to login on error
            navigate('/login', { replace: true });
            return;
          }
          
          if (data?.session) {
            console.log("MagicLinkHandler - Successfully authenticated with magic link");
            
            // Create or get app user
            try {
              await getOrCreateAppUser();
              console.log("MagicLinkHandler - App user created/retrieved successfully");
            } catch (userError) {
              console.error("MagicLinkHandler - Error creating/retrieving app user:", userError);
            }
            
            // Wait for the authentication state to propagate through the auth listener
            // This is crucial to prevent the "not authenticated" state
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // Verify the session is actually established
            const { data: sessionCheck } = await supabase.auth.getSession();
            if (!sessionCheck.session) {
              console.error("MagicLinkHandler - Session not established after exchange");
              toast({
                title: "Session Error",
                description: "Please try logging in again.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top",
              });
              navigate('/login', { replace: true });
              return;
            }
            
            // Dispatch a custom event to ensure the main App component updates its auth state
            // This helps with any potential timing issues
            window.dispatchEvent(new CustomEvent('authStateForceUpdate', { 
              detail: { session: sessionCheck.session } 
            }));
            
            // Check if there's a specific redirect path in the user metadata
            const redirectPath = data.session.user?.user_metadata?.redirectAfterAuth || '/dashboard';
            console.log(`MagicLinkHandler - Redirecting to: ${redirectPath}`);
            
            // Navigate to the target path with history replacement
            // This prevents users from navigating back to the magic link URL
            navigate(redirectPath, { replace: true });
          } else {
            console.error("MagicLinkHandler - No session data returned after code exchange");
            navigate('/login', { replace: true });
          }
        } catch (err) {
          console.error("MagicLinkHandler - Exception during magic link auth:", err);
          navigate('/login', { replace: true });
        }
      }
    };
    
    // Only run if we have search params or hash
    if (location.search || window.location.hash) {
      handleMagicLinkAuth();
    }
  }, [location.search, location.pathname, navigate, toast]);

  // This component doesn't render anything
  return null;
};

export default MagicLinkHandler; 